import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type H1H2AmpelStatus =
    | 'keine_daten'
    | 'nicht_kostendeckend'
    | 'kostendeckend'
    | 'ziel_erreicht';

export interface H1H2HalfMetrics {
    label: 'H1' | 'H2';
    activeMonths: number;
    umsatzNettoSumme: number;
    umsatzNettoAvg: number;
    umsatzBruttoSumme: number;
    umsatzBruttoAvg: number;
    gewinnSumme: number;
    gewinnAvg: number;
    ruecklagenDeltaSumme: number;
    ruecklagenDeltaAvg: number;
    umsatzauslastung: number;
    avgProzent: number;
    urlaubTageSumme: number;
    krankTageSumme: number;
    ampel: H1H2AmpelStatus;
}

export interface H1H2ComparisonModalProps {
    isOpen: boolean;
    onClose: () => void;
    currencyCode: string;
    ruecklagenZielFaktor: number;
    h1: H1H2HalfMetrics;
    h2: H1H2HalfMetrics;
}

const UI_SCALE_MIN = 0.75;
const UI_SCALE_MAX = 1.45;
const UI_SCALE_STEP = 0.05;
const SIZE_STORAGE_KEY = 'pe_h1h2_modal_ui_scale';

function resolveCurrency(code?: string): string {
    const c = String(code || 'EUR').trim().toUpperCase();
    return c === 'CHF' ? 'CHF' : 'EUR';
}

const formatMoney = (value: number, currencyCode: string) =>
    new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: resolveCurrency(currencyCode),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true,
    }).format(value || 0);

const formatPctRatio = (ratio: number, decimals = 1) =>
    ((Number(ratio) || 0) * 100).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }) + '%';

const formatDays = (value: number) =>
    (Number(value) || 0).toLocaleString('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    }) + ' T.';

const formatSignedMoney = (value: number, currencyCode: string) => {
    const n = Number(value) || 0;
    const prefix = n > 0 ? '+' : '';
    return prefix + formatMoney(n, currencyCode);
};

const formatSignedPp = (ratioDiff: number, decimals = 1) => {
    const pp = (Number(ratioDiff) || 0) * 100;
    const prefix = pp > 0 ? '+' : '';
    return (
        prefix +
        pp.toLocaleString('de-DE', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }) +
        ' PP'
    );
};

const formatSignedDays = (value: number) => {
    const n = Number(value) || 0;
    const prefix = n > 0 ? '+' : '';
    return prefix + formatDays(n);
};

const AMPEL_META: Record<
    H1H2AmpelStatus,
    { label: string; color: string; border: string; bg: string; icon: React.ReactNode }
> = {
    keine_daten: {
        label: 'Keine Daten',
        color: 'text-stone-600 dark:text-slate-300',
        border: 'border-stone-300 dark:border-slate-600',
        bg: 'bg-stone-100 dark:bg-slate-800/50',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
            </svg>
        ),
    },
    nicht_kostendeckend: {
        label: 'Nicht kostendeckend',
        color: 'text-red-700 dark:text-red-300',
        border: 'border-red-300 dark:border-red-500/50',
        bg: 'bg-red-50 dark:bg-red-950/35',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.738 3h4.017c.993 0 1.854-.656 2.148-1.595l2 6A2 2 0 0116.75 14v5a2 2 0 01-2-2h-2.5a2 2 0 01-2-2v-5z" />
            </svg>
        ),
    },
    kostendeckend: {
        label: 'Kostendeckend',
        color: 'text-amber-800 dark:text-amber-300',
        border: 'border-amber-300 dark:border-amber-500/50',
        bg: 'bg-amber-50 dark:bg-amber-950/35',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
    ziel_erreicht: {
        label: 'Ziel erreicht',
        color: 'text-emerald-800 dark:text-emerald-300',
        border: 'border-emerald-300 dark:border-emerald-500/50',
        bg: 'bg-emerald-50 dark:bg-emerald-950/35',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
};

function readStoredScale(): number {
    try {
        const raw = localStorage.getItem(SIZE_STORAGE_KEY);
        const n = raw ? parseFloat(raw) : 1;
        if (!Number.isFinite(n)) return 1;
        return Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, Math.round(n * 100) / 100));
    } catch {
        return 1;
    }
}

function HalfColumn({
    metrics,
    currencyCode,
    ruecklagenZielFaktor,
}: {
    metrics: H1H2HalfMetrics;
    currencyCode: string;
    ruecklagenZielFaktor: number;
}) {
    const empty = metrics.activeMonths === 0;
    const ampel = AMPEL_META[metrics.ampel];

    return (
        <div
            className={`rounded-xl border border-pe-border dark:border-slate-800 bg-pe-elevated dark:bg-slate-950/50 p-3 space-y-2.5 ${
                empty ? 'opacity-55' : ''
            }`}
        >
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-black uppercase tracking-widest text-pe-accent-text">
                    {metrics.label}
                </h3>
                <span className="rounded-full border border-pe-border dark:border-slate-700 bg-pe-card dark:bg-slate-800/80 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-pe-text dark:text-slate-200">
                    {metrics.activeMonths} Mon. aktiv
                </span>
            </div>

            <div
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${ampel.border} ${ampel.bg}`}
            >
                <div className={`shrink-0 ${ampel.color}`}>{ampel.icon}</div>
                <span className={`text-sm font-black uppercase tracking-wide ${ampel.color}`}>
                    {ampel.label}
                </span>
            </div>

            <div className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-[11px] font-semibold text-pe-muted dark:text-slate-300">Abweichung</div>
                        <div className="text-xl font-black font-mono tabular-nums text-pe-accent-text leading-tight">
                            {formatPctRatio(metrics.avgProzent, 1)}
                        </div>
                        <div className="text-[11px] text-pe-muted dark:text-slate-400">
                            Ziel {formatPctRatio(ruecklagenZielFaktor, 0)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] font-semibold text-pe-muted dark:text-slate-300">Auslastung</div>
                        <div className="text-xl font-black font-mono tabular-nums text-pe-accent-text leading-tight">
                            {formatPctRatio(metrics.umsatzauslastung, 1)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-slate-900/80 px-3 py-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Monatsumsatz · Durchschnitt netto
                </div>
                <div className="text-xl font-black font-mono tabular-nums text-pe-text dark:text-white leading-tight mt-0.5">
                    {formatMoney(metrics.umsatzNettoAvg, currencyCode)}
                </div>
                <div className="mt-1.5 space-y-0.5 text-[12px] leading-snug">
                    <div className="flex justify-between gap-2 text-pe-text dark:text-slate-300">
                        <span>Ø brutto / Monat</span>
                        <span className="font-mono font-semibold tabular-nums">
                            {formatMoney(metrics.umsatzBruttoAvg, currencyCode)}
                        </span>
                    </div>
                    <div className="flex justify-between gap-2 text-pe-muted dark:text-slate-400">
                        <span>Summe netto HJ</span>
                        <span className="font-mono font-semibold tabular-nums">
                            {formatMoney(metrics.umsatzNettoSumme, currencyCode)}
                        </span>
                    </div>
                    <div className="flex justify-between gap-2 text-pe-muted dark:text-slate-400">
                        <span>Summe brutto HJ</span>
                        <span className="font-mono font-semibold tabular-nums">
                            {formatMoney(metrics.umsatzBruttoSumme, currencyCode)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-slate-900/80 px-3 py-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Monatsergebnis · Ø Gesamtgewinn
                </div>
                <div className="text-xl font-black font-mono tabular-nums text-pe-text dark:text-white leading-tight mt-0.5">
                    {formatMoney(metrics.gewinnAvg, currencyCode)}
                </div>
                <div className="mt-1.5 space-y-0.5 text-[12px] leading-snug">
                    <div className="flex justify-between gap-2 text-pe-text dark:text-slate-300">
                        <span>Ø Rücklagen-Delta / Monat</span>
                        <span className="font-mono font-semibold tabular-nums">
                            {formatMoney(metrics.ruecklagenDeltaAvg, currencyCode)}
                        </span>
                    </div>
                    <div className="flex justify-between gap-2 text-pe-muted dark:text-slate-400">
                        <span>Summe Gewinn HJ</span>
                        <span className="font-mono font-semibold tabular-nums">
                            {formatMoney(metrics.gewinnSumme, currencyCode)}
                        </span>
                    </div>
                    <div className="flex justify-between gap-2 text-pe-muted dark:text-slate-400">
                        <span>Summe Rücklagen HJ</span>
                        <span className="font-mono font-semibold tabular-nums">
                            {formatMoney(metrics.ruecklagenDeltaSumme, currencyCode)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-slate-900/80 px-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-[11px] font-semibold text-pe-muted dark:text-slate-300">Urlaub HJ</div>
                        <div className="text-lg font-black font-mono tabular-nums text-pe-text dark:text-white leading-tight">
                            {formatDays(metrics.urlaubTageSumme)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] font-semibold text-pe-muted dark:text-slate-300">Krank HJ</div>
                        <div className="text-lg font-black font-mono tabular-nums text-orange-700 dark:text-orange-300 leading-tight">
                            {formatDays(metrics.krankTageSumme)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const H1H2ComparisonModal: React.FC<H1H2ComparisonModalProps> = ({
    isOpen,
    onClose,
    currencyCode,
    ruecklagenZielFaktor,
    h1,
    h2,
}) => {
    const [uiScale, setUiScale] = useState(readStoredScale);
    const currency = resolveCurrency(currencyCode);

    useEffect(() => {
        try {
            localStorage.setItem(SIZE_STORAGE_KEY, String(uiScale));
        } catch {
            /* ignore */
        }
    }, [uiScale]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const adjustUiScale = useCallback((delta: number) => {
        setUiScale((s) =>
            Math.round(Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, s + delta)) * 100) / 100
        );
    }, []);

    if (!isOpen || typeof document === 'undefined') return null;

    const h2Empty = h2.activeMonths === 0;

    const delta = {
        nettoAvg: h2.umsatzNettoAvg - h1.umsatzNettoAvg,
        gewinnAvg: h2.gewinnAvg - h1.gewinnAvg,
        ruecklagenDeltaAvg: h2.ruecklagenDeltaAvg - h1.ruecklagenDeltaAvg,
        avgProzent: h2.avgProzent - h1.avgProzent,
        umsatzauslastung: h2.umsatzauslastung - h1.umsatzauslastung,
        urlaub: h2.urlaubTageSumme - h1.urlaubTageSumme,
        krank: h2.krankTageSumme - h1.krankTageSumme,
    };

    return createPortal(
        <div
            className="pe-h1h2-modal-overlay fixed inset-0 z-[99999] flex items-start justify-center pt-6 sm:pt-10 pb-4 px-3 bg-stone-900/45 dark:bg-slate-950/80 backdrop-blur-sm overflow-auto"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="pe-h1h2-modal bg-pe-card dark:bg-slate-900 border border-pe-border-strong dark:border-slate-800 rounded-2xl w-[min(56rem,100%)] text-pe-text dark:text-slate-100 shadow-2xl flex flex-col my-auto"
                style={{ zoom: uiScale } as React.CSSProperties}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="h1h2-comparison-title"
            >
                <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2 border-b border-pe-border dark:border-slate-800 shrink-0 bg-pe-card dark:bg-slate-900">
                    <div className="min-w-0">
                        <h2
                            id="h1h2-comparison-title"
                            className="text-lg font-black uppercase tracking-widest text-pe-accent-text"
                        >
                            Halbjahres-Vergleich
                        </h2>
                        <p className="text-[12px] text-pe-muted dark:text-slate-400 font-medium">
                            H1 vs. H2 · große Zahl = Monatsdurchschnitt · {currency}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div
                            className="inline-flex items-center gap-1 rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-slate-950/80 px-1.5 py-0.5"
                            title="Darstellung verkleinern / vergrößern"
                        >
                            <button
                                type="button"
                                disabled={uiScale <= UI_SCALE_MIN + 0.001}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    adjustUiScale(-UI_SCALE_STEP);
                                }}
                                aria-label="Verkleinern"
                                className="w-7 h-7 rounded text-pe-accent-text font-black text-lg leading-none hover:bg-[#E6C299]/15 disabled:opacity-35 disabled:cursor-default"
                            >
                                −
                            </button>
                            <span className="min-w-[42px] text-center text-xs font-extrabold text-pe-muted dark:text-slate-300 tabular-nums">
                                {Math.round(uiScale * 100)}%
                            </span>
                            <button
                                type="button"
                                disabled={uiScale >= UI_SCALE_MAX - 0.001}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    adjustUiScale(UI_SCALE_STEP);
                                }}
                                aria-label="Vergrößern"
                                className="w-7 h-7 rounded text-pe-accent-text font-black text-lg leading-none hover:bg-[#E6C299]/15 disabled:opacity-35 disabled:cursor-default"
                            >
                                +
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            className="rounded-lg border border-pe-border-strong dark:border-slate-700 bg-pe-elevated dark:bg-slate-800 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-pe-text dark:text-slate-200 hover:border-[#E6C299]/50 dark:hover:border-slate-500 transition-colors"
                        >
                            Schließen
                        </button>
                    </div>
                </div>

                <div className="px-4 py-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <HalfColumn
                            metrics={h1}
                            currencyCode={currency}
                            ruecklagenZielFaktor={ruecklagenZielFaktor}
                        />
                        <HalfColumn
                            metrics={h2}
                            currencyCode={currency}
                            ruecklagenZielFaktor={ruecklagenZielFaktor}
                        />
                    </div>

                    <div
                        className={`mt-3 rounded-xl border border-pe-border dark:border-slate-800 bg-pe-elevated dark:bg-slate-950/60 px-3 py-2.5 ${
                            h2Empty ? 'opacity-45' : ''
                        }`}
                    >
                        <div className="text-[11px] font-bold uppercase tracking-widest text-pe-muted dark:text-slate-400 mb-2">
                            Delta H2 vs. H1
                        </div>
                        {h2Empty ? (
                            <p className="text-sm text-pe-muted dark:text-slate-400 font-medium">Noch keine Daten für H2</p>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-3 gap-y-2 text-[12px]">
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Netto Ø</div>
                                    <div className="font-black font-mono tabular-nums text-pe-text dark:text-white text-sm">
                                        {formatSignedMoney(delta.nettoAvg, currency)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Gewinn Ø</div>
                                    <div className="font-black font-mono tabular-nums text-pe-text dark:text-white text-sm">
                                        {formatSignedMoney(delta.gewinnAvg, currency)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Rücklagen Ø</div>
                                    <div className="font-black font-mono tabular-nums text-pe-text dark:text-white text-sm">
                                        {formatSignedMoney(delta.ruecklagenDeltaAvg, currency)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Abweichung</div>
                                    <div className="font-black font-mono tabular-nums text-pe-accent-text text-sm">
                                        {formatSignedPp(delta.avgProzent)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Auslastung</div>
                                    <div className="font-black font-mono tabular-nums text-pe-accent-text text-sm">
                                        {formatSignedPp(delta.umsatzauslastung)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Urlaub</div>
                                    <div className="font-black font-mono tabular-nums text-pe-text dark:text-white text-sm">
                                        {formatSignedDays(delta.urlaub)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-pe-muted dark:text-slate-400 font-semibold">Krank</div>
                                    <div className="font-black font-mono tabular-nums text-orange-700 dark:text-orange-300 text-sm">
                                        {formatSignedDays(delta.krank)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default H1H2ComparisonModal;
